<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        :max-width="isMobile ? '90vw' : maxWidth"
        :style="zIndex !== null ? 'z-index: ' + zIndex + ';' : null"
    >
        <v-card
            class="d-flex flex-column responsive-dialog-card"
            :class="[{'responsive-dialog-framed': framed}, cardClass]"
            :color="cardColor || undefined"
            :style="(isMobile && fullHeight ? 'height: calc(88vh - ' + bottomInset + 'px); height: calc(88dvh - ' + bottomInset + 'px); margin-bottom: ' + bottomInset + 'px; position: relative;' : 'position: relative;') + ' overflow: hidden;'"
        >
            <!-- Rohove ornamenty rameckuji celou kartu, zaviraci krizek (vyssi z-index) je prekryva primo v rohu. Cisté dekorace, zadna interakce. -->
            <div class="responsive-dialog-corner tl"/>
            <div class="responsive-dialog-corner tr"/>
            <div class="responsive-dialog-corner bl"/>
            <div class="responsive-dialog-corner br"/>
            <!-- Volitelna syta hlavicka s ikonou - popup ji dostane jen kdyz posle headerTitle, jinak beze zmeny (kompatibilita s popupy, co maji vlastni titulek primo v obsahu, napr. SkillDetailPopup). -->
            <div
                v-if="headerTitle"
                class="responsive-dialog-header"
                :class="[{'responsive-dialog-header--overlap': headerImage && headerImageOverlap}, headerClass]"
                :style="(headerColor || cardColor) ? {backgroundColor: headerColor || cardColor, color: headerTextColor} : null"
                @touchstart.passive="$emit('header-touchstart', $event)"
                @touchend="$emit('header-touchend', $event)"
            >
                <!-- header-touchstart/header-touchend cistě přeposílají native touch eventy z hlavičky ven jako Vue eventy (volitelne pro swipe-to-close, napr. PlayerChatPopup) - nikdo jiny je dnes neposlouchá. -->
                <img
                    v-if="headerImage"
                    :src="headerImage"
                    class="responsive-dialog-header__image"
                    :class="{'responsive-dialog-header__image--overlap': headerImageOverlap}"
                    alt=""
                />
                <span v-else-if="headerIcon" class="responsive-dialog-header__icon">{{ headerIcon }}</span>
                <span class="responsive-dialog-header__title">{{ headerTitle }}</span>
            </div>
            <slot/>
            <!-- Velky zaviraci krizek vpravo dole; pravy horni roh je rezervovany pro new-day. Dialog neni fullscreen, klik na backdrop taky zavira. color/zIndex/cardColor jsou volitelne (default = puvodni chovani). cardColor jde pres Vuetify :color prop, ne :style - v-card ma vlastni render, ktery externi :style binding na sve koreni tise ignoruje. -->
            <v-btn
                v-if="isMobile && !hideDefaultClose"
                fab
                width="45"
                height="45"
                :color="color"
                class="btn-shadow"
                :style="'position: absolute; bottom: 16px; ' + closeButtonSide + ': 16px; z-index: ' + ((zIndex !== null ? Number(zIndex) : 1200) + 50) + ';'"
                @click="$emit('input', false)"
            >
                <v-icon size="29" color="black">mdi-close</v-icon>
            </v-btn>
        </v-card>
    </v-dialog>
</template>

<script>
    // Sdileny UI-kit dialog - nahrazuje ad-hoc v-dialog/fullscreen. Na mobilu viditelny klikatelny backdrop + velky krizek, na desktopu bezna velikost centrovaneho dialogu.
    import {isMobileMixin} from '@/utils/isMobileMixin';
    import {flags} from '@/config/featureFlags';

    export default {
        name: 'ResponsiveDialog',
        mixins: [isMobileMixin],
        computed: {
            // Zrcadleni pro praváky - vychozi (flag off) je krizek vpravo dole, daleko od palce leváka. ResponsiveDialog je sdileny prakticky vsemi popupy v appce, takze tahle jedna zmena zrcadli krizek vsude najednou.
            closeButtonSide() {
                return flags.rightHanded ? 'left' : 'right';
            },
        },
        props: {
            value: {type: Boolean, default: false},
            maxWidth: {type: [String, Number], default: 480},
            // rámeček kolem popupu, volitelné - hodí se hlavně u popupů s celoplošným pozadím (CityTravelDialog)
            framed: {type: Boolean, default: false},
            // barva zavíracího křížku - default oranžová
            color: {type: String, default: 'warning'},
            // skryje vychozi zaviraci FAB krizek, kdyz si popup rendruje vlastni zavirani jinak (napr. ConfirmDialog v iconButtons rezimu)
            hideDefaultClose: {type: Boolean, default: false},
            // Default null = zadny inline z-index styl na <v-dialog> - jakakoliv hodnota tam totiz unikne i na Vuetify vlastni koren a rozbije mu interni z-index stacking (klik mimo dialog ho pak prestane zavirat). Zadat jen pro vnoreni nad jiny ResponsiveDialog (SkillDetailPopup nad SkillPopup).
            zIndex: {type: [String, Number], default: null},
            // barva pozadi vnitrni v-card (napr. dle typu skillu), jde pres Vuetify color prop (viz v-card vyse)
            cardColor: {type: String, default: ''},
            // dodatecny odstup zdola na mobilu (px), kdyz na obrazovce pod dialogem je fixni prvek (napr. TeamBottomSheet handle)
            bottomInset: {type: [String, Number], default: 0},
            // na mobilu vynuti 88vh/88dvh vysku karty - vypnout pro kratky obsah (napr. SkillDetailPopup), ktery se ma prizpusobit obsahu
            fullHeight: {type: Boolean, default: true},
            // Volitelna syta hlavicka - emoji/ikona + titulek. Prazdne headerTitle (default) = zadna hlavicka.
            headerIcon: {type: String, default: ''},
            // Obrazek misto emoji ikony, ma prednost pred headerIcon
            headerImage: {type: String, default: ''},
            // Velky obrazek jako "levy sloupec", co prekryva kus hlavicky i tela - telo si musi rezervovat misto samo (viz ConfirmDialog.vue)
            headerImageOverlap: {type: Boolean, default: false},
            headerTitle: {type: String, default: ''},
            // barva hlavicky - kdyz neni zadana, spadne na cardColor. Musi byt platna CSS hodnota (hex/rgb), ne Vuetify nazev barvy - jde primo do raw :style bindingu, ktery Vuetify nazvy nezna.
            headerColor: {type: String, default: ''},
            // barva textu hlavicky - svetle hlavicky (napr. amber) potrebuji cerny text
            headerTextColor: {type: String, default: '#fff'},
            // Skin/material system (viz docs/superpowers/specs/2026-08-04-theme-skin-system-design.md) - CSS trida pro hlavicku, kdyz ji ma ridit externi skin stylesheet misto headerColor/headerTextColor (ty maji prednost, viz :style binding v template)
            headerClass: {type: [String, Array, Object], default: null},
            // Stejny princip jako headerClass, pro cely ramecek karty - skin muze pres CSS promenne --skin-card-radius/--skin-corner-radius prepsat tvar ramecku i rohovych ornamentu
            cardClass: {type: [String, Array, Object], default: null},
        },
    }
</script>

<style scoped>
/* "Trubkovy" 3D ramecek docasne odstranen - trida .responsive-dialog-framed se porad vaze (viz template), takze jde snadno vratit/nahradit. */

/* Jemne stinovani karty jako background-image PRIMO na karte (pod obsahem) - v mistech s vlastnim nepruhlednym obsahem (bila tabulka, tlacitka) ten prirozene prekryje pozadi a zustane cisty, efekt je videt jen na skutecnem pozadi. */
/* Vuetify interne rendruje kartu do vlastniho .v-dialog wrapperu MIMO sablonu teto komponenty (scoped selektor by ho nezasahl, viz Vuetify VDialog.js render() - koren je .v-dialog__container, ne vnitrni .v-dialog div) - ::v-deep .v-dialog:has(.skin-card) cili na nej primo, at zustane synchronni s .responsive-dialog-card border-radius nize. */
::v-deep .v-dialog:has(.skin-card) {
    border-radius: var(--skin-card-radius, 22px) !important;
}

.responsive-dialog-card {
    /* Zaobleni karty stejne velke jako polomer zaviraciho krizku (45px fab -> 22.5px). --skin-card-radius umoznuje skinu prepsat tvar. */
    border-radius: var(--skin-card-radius, 22px) !important;
    background-image:
        radial-gradient(circle at 50% 0%, rgba(33,168,122,0.1) 0%, transparent 55%),
        linear-gradient(
            140deg,
            rgba(0,0,0,0.02) 0%,
            rgba(0,0,0,0.22) 25%,
            rgba(0,0,0,0.02) 50%,
            rgba(0,0,0,0.22) 75%,
            rgba(0,0,0,0.02) 100%
        );
}

/* Rohove ornamenty + volitelna hlavicka, sdilene napric vsemi ResponsiveDialog popupy. Rohy jdou az k dolnimu okraji karty, zaviraci krizek (vyssi z-index, viz template) je prekryva primo v rohu - schvalne, ne kolize. */
.responsive-dialog-corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.7);
    pointer-events: none;
    /* z-index 3, at znacky nejsou prekryvane vnitrnim obsahem popupu (napr. dlazdice mest v CityTravelDialog). */
    z-index: 3;
}
/* !important nutne - znacky jsou prvni potomek .v-card, na ktery Vuetify globalne aplikuje vyssi specificitou border-radius:inherit. --skin-corner-radius se deduje z .skin-card na predkovi (.responsive-dialog-card), CSS promenne se dedi dolu DOM stromem samy. */
.responsive-dialog-corner.tl { top: 12px; left: 12px; border-right: none; border-bottom: none; border-radius: var(--skin-corner-radius, 10px) 0 0 0 !important; }
.responsive-dialog-corner.tr { top: 12px; right: 12px; border-left: none; border-bottom: none; border-radius: 0 var(--skin-corner-radius, 10px) 0 0 !important; }
.responsive-dialog-corner.bl { bottom: 12px; left: 12px; border-right: none; border-top: none; border-radius: 0 0 0 var(--skin-corner-radius, 10px) !important; }
.responsive-dialog-corner.br { bottom: 12px; right: 12px; border-left: none; border-top: none; border-radius: 0 0 var(--skin-corner-radius, 10px) 0 !important; }

.responsive-dialog-header {
    /* Zamerne bez color/background-color natvrdo - barvu/pozadi resi bud inline :style (headerColor/headerTextColor props) nebo headerClass (skin trida); natvrdo "color" by je prebilo pro vsechny volajici bez headerColor. */
    font-weight: 700;
    font-size: 1.47rem;
    text-align: center;
    padding: 10px 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    /* Odpovida zaobleni karty, at hranate horni rohy hlavicky nevykukuji pres zaobleny okraj karty. */
    border-radius: var(--skin-card-radius, 22px) var(--skin-card-radius, 22px) 0 0;
    flex: 0 0 auto;
    /* Stejne "zihani" jako telo karty vyse - barva hlavicky jde jen pres background-color (ne shorthand "background", ten by tento background-image vynuloval). */
    background-image:
        radial-gradient(circle at 50% 0%, rgba(33,168,122,0.1) 0%, transparent 55%),
        linear-gradient(
            140deg,
            rgba(0,0,0,0.02) 0%,
            rgba(0,0,0,0.22) 25%,
            rgba(0,0,0,0.02) 50%,
            rgba(0,0,0,0.22) 75%,
            rgba(0,0,0,0.02) 100%
        );
}
.responsive-dialog-header__icon {
    font-size: 1.2rem;
    line-height: 1;
}
.responsive-dialog-header__image {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    object-position: top center;
    /* Radialni gradient prosviti skrz pruhledne casti PNG a pusobi jako 3D zlata koule za postavou. Stejna paleta jako .tfw-knowegle-frame v TrainerFightWinNext.vue. */
    background: radial-gradient(circle at 35% 30%, #FFECB3 0%, #FFB300 55%, #FF8F00 100%);
    box-shadow: 0 0 14px 4px rgba(255,193,7,0.85), 0 2px 6px rgba(0,0,0,0.3);
    flex: 0 0 auto;
    margin: -4px 0;
}
/* "Levy sloupec" varianta - obrazek vypadne z flex toku (position:absolute), header si rezervuje misto paddingem. Telo pod hlavickou si stejne rezervovane misto musi pridat samo (viz ConfirmDialog.vue) - ResponsiveDialog nevi, co je v slotu. */
.responsive-dialog-header--overlap {
    /* Titulek obrazek ignoruje a centruje se na celou sirku hlavicky - obrazek je cisty absolutne pozicovany prekryv, nemeni layout textu. */
    position: relative;
    min-height: 64px;
}
.responsive-dialog-header__image--overlap {
    position: absolute;
    left: 20px;
    top: 10px;
    width: 128px;
    height: 128px;
    margin: 0;
    /* Pod titulkem (z-index 2 nize), ale nad pozadim hlavicky - staticke flex polozky se chovaji jako z-index:0. */
    z-index: 1;
}
.responsive-dialog-header__title {
    position: relative;
    z-index: 2;
}
</style>
